import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  WritableSignal,
} from '@angular/core';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';

interface IRgbColor {
  red: number;
  green: number;
  blue: number;
}

interface IHsvColor {
  hue: number;
  saturation: number;
  brightness: number;
}

@Component({
  selector: 'aw-color-picker',
  imports: [NgTemplateOutlet, OverlayModule],
  templateUrl: './color-picker.component.html',
  styleUrl: './color-picker.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorPickerComponent {
  public readonly value: InputSignal<string> = input.required<string>();
  public readonly inline: InputSignal<boolean> = input(false);
  public readonly valueChange: OutputEmitterRef<string> = output<string>();
  public readonly closed: OutputEmitterRef<void> = output<void>();

  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );

  protected readonly hue: WritableSignal<number> = signal(0);
  protected readonly saturation: WritableSignal<number> = signal(0);
  protected readonly brightness: WritableSignal<number> = signal(1);
  protected readonly hexValue: WritableSignal<string> = signal('FFFFFF');
  protected readonly overlayPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 6,
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: -6,
    },
  ];

  private lastEmittedValue: string = '';

  constructor() {
    effect((): void => {
      const value: string = this.normalizeHex(this.value());
      if (!this.isValidHex(value) || value === this.lastEmittedValue) {
        return;
      }
      this.setFromHex(value);
    });
  }

  protected selectSaturationAndBrightness(event: PointerEvent): void {
    const element: HTMLElement = event.currentTarget as HTMLElement;
    element.setPointerCapture(event.pointerId);
    this.updateSaturationAndBrightness(event, element);
  }

  protected moveSaturationAndBrightness(event: PointerEvent): void {
    if (event.buttons !== 1) {
      return;
    }
    this.updateSaturationAndBrightness(
      event,
      event.currentTarget as HTMLElement
    );
  }

  protected selectHue(event: PointerEvent): void {
    const element: HTMLElement = event.currentTarget as HTMLElement;
    element.setPointerCapture(event.pointerId);
    this.updateHue(event, element);
  }

  protected moveHue(event: PointerEvent): void {
    if (event.buttons !== 1) {
      return;
    }
    this.updateHue(event, event.currentTarget as HTMLElement);
  }

  protected updateHexValue(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    const value: string = this.normalizeHex(input.value);
    this.hexValue.set(value);
    if (this.isValidHex(value)) {
      this.setFromHex(value);
      this.emitValue(value);
    }
  }

  protected hueColor(): string {
    return `hsl(${this.hue()}, 100%, 50%)`;
  }

  protected selectedColor(): string {
    return `#${this.hexValue()}`;
  }

  private updateSaturationAndBrightness(
    event: PointerEvent,
    element: HTMLElement
  ): void {
    const bounds: DOMRect = element.getBoundingClientRect();
    this.saturation.set(
      this.clamp((event.clientX - bounds.left) / bounds.width)
    );
    this.brightness.set(
      1 - this.clamp((event.clientY - bounds.top) / bounds.height)
    );
    this.emitHsvValue();
  }

  private updateHue(event: PointerEvent, element: HTMLElement): void {
    const bounds: DOMRect = element.getBoundingClientRect();
    this.hue.set(
      Math.round(this.clamp((event.clientX - bounds.left) / bounds.width) * 360)
    );
    this.emitHsvValue();
  }

  private emitHsvValue(): void {
    const rgb: IRgbColor = this.hsvToRgb({
      hue: this.hue(),
      saturation: this.saturation(),
      brightness: this.brightness(),
    });
    const value: string = this.rgbToHex(rgb);
    this.hexValue.set(value);
    this.emitValue(value);
  }

  private emitValue(value: string): void {
    this.lastEmittedValue = value;
    this.valueChange.emit(value);
  }

  private setFromHex(value: string): void {
    const hsv: IHsvColor = this.rgbToHsv(this.hexToRgb(value));
    this.hue.set(hsv.hue);
    this.saturation.set(hsv.saturation);
    this.brightness.set(hsv.brightness);
    this.hexValue.set(value.toUpperCase());
  }

  private normalizeHex(value: string): string {
    return value
      .replace(/#/g, '')
      .replace(/[^0-9a-f]/gi, '')
      .slice(0, 6)
      .toUpperCase();
  }

  private isValidHex(value: string): boolean {
    return /^[0-9A-F]{6}$/.test(value);
  }

  private clamp(value: number): number {
    return Math.min(1, Math.max(0, value));
  }

  private hexToRgb(value: string): IRgbColor {
    return {
      red: Number.parseInt(value.slice(0, 2), 16),
      green: Number.parseInt(value.slice(2, 4), 16),
      blue: Number.parseInt(value.slice(4, 6), 16),
    };
  }

  private rgbToHex({ red, green, blue }: IRgbColor): string {
    return [red, green, blue]
      .map((value: number): string =>
        Math.round(value).toString(16).padStart(2, '0')
      )
      .join('')
      .toUpperCase();
  }

  private rgbToHsv({ red, green, blue }: IRgbColor): IHsvColor {
    const normalizedRed: number = red / 255;
    const normalizedGreen: number = green / 255;
    const normalizedBlue: number = blue / 255;
    const maximum: number = Math.max(
      normalizedRed,
      normalizedGreen,
      normalizedBlue
    );
    const minimum: number = Math.min(
      normalizedRed,
      normalizedGreen,
      normalizedBlue
    );
    const delta: number = maximum - minimum;
    let hue = 0;

    if (delta !== 0) {
      if (maximum === normalizedRed) {
        hue = 60 * (((normalizedGreen - normalizedBlue) / delta) % 6);
      } else if (maximum === normalizedGreen) {
        hue = 60 * ((normalizedBlue - normalizedRed) / delta + 2);
      } else {
        hue = 60 * ((normalizedRed - normalizedGreen) / delta + 4);
      }
    }

    return {
      hue: hue < 0 ? hue + 360 : hue,
      saturation: maximum === 0 ? 0 : delta / maximum,
      brightness: maximum,
    };
  }

  private hsvToRgb({ hue, saturation, brightness }: IHsvColor): IRgbColor {
    const chroma: number = brightness * saturation;
    const hueSection: number = hue / 60;
    const secondComponent: number =
      chroma * (1 - Math.abs((hueSection % 2) - 1));
    const offset: number = brightness - chroma;
    let red = 0;
    let green = 0;
    let blue = 0;

    if (hueSection < 1) {
      red = chroma;
      green = secondComponent;
    } else if (hueSection < 2) {
      red = secondComponent;
      green = chroma;
    } else if (hueSection < 3) {
      green = chroma;
      blue = secondComponent;
    } else if (hueSection < 4) {
      green = secondComponent;
      blue = chroma;
    } else if (hueSection < 5) {
      red = secondComponent;
      blue = chroma;
    } else {
      red = chroma;
      blue = secondComponent;
    }

    return {
      red: (red + offset) * 255,
      green: (green + offset) * 255,
      blue: (blue + offset) * 255,
    };
  }
}
